"use client";

import { useState, useEffect, ReactNode } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";
import {
  isInAppBrowser,
  copyToClipboard,
  openInExternalBrowser,
} from "@/lib/inapp-browser";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * 인앱 브라우저 안내 콘텐츠 (배너와 다이얼로그에서 공유)
 *
 * 배너(`InAppBrowserGuide`)·`InAppSignInButton` 다이얼로그 외에, 명령형 `openSignIn`
 * 으로 로그인을 트리거하는 버튼(예: `StudyStartButton`)도 인앱 브라우저일 때 이 콘텐츠를
 * 다이얼로그로 재사용한다.
 */
export function InAppBrowserGuideContent() {
  const [isCopied, setIsCopied] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  // navigator 외부 시스템 동기화
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsAndroid(/Android/i.test(navigator.userAgent || ''));
  }, []);

  const handleOpenExternal = () => {
    openInExternalBrowser(window.location.href);
  };

  const handleCopy = async () => {
    const success = await copyToClipboard(window.location.href);
    if (success) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-amber-700 dark:text-amber-300">
        네이버, 인스타그램 등의 앱 내 브라우저에서는 구글 정책상 로그인이
        제한됩니다. 크롬이나 사파리에서 열어주세요.
      </p>

      <div className="flex gap-2">
        {isAndroid && (
          <button
            onClick={handleOpenExternal}
            className="flex-1 flex items-center justify-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg py-2.5 px-3 text-sm font-medium transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            크롬으로 열기
          </button>
        )}
        <button
          onClick={handleCopy}
          className={`flex items-center justify-center gap-1.5 rounded-lg py-2.5 px-3 text-sm font-medium transition-colors ${
            isAndroid
              ? 'bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 hover:bg-amber-50 dark:hover:bg-gray-700'
              : 'flex-1 bg-amber-600 hover:bg-amber-700 text-white'
          }`}
        >
          {isCopied ? (
            <Check className={`w-3.5 h-3.5 ${isAndroid ? 'text-green-500' : 'text-green-200'}`} />
          ) : (
            <Copy className={`w-3.5 h-3.5 ${isAndroid ? 'text-amber-700 dark:text-amber-300' : ''}`} />
          )}
          <span
            className={
              isCopied
                ? isAndroid ? 'text-green-600 dark:text-green-400' : 'text-green-200'
                : isAndroid ? 'text-amber-700 dark:text-amber-300' : ''
            }
          >
            {isCopied ? '복사됨! 브라우저에 붙여넣기 해주세요' : 'URL 복사하기'}
          </span>
        </button>
      </div>
    </div>
  );
}

/**
 * 배너형 안내 - sign-in, sign-up 페이지에서 사용
 * 인앱 브라우저일 때만 렌더링됨
 */
export default function InAppBrowserGuide() {
  const [isInApp, setIsInApp] = useState(false);

  // navigator 외부 시스템 동기화
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsInApp(isInAppBrowser());
  }, []);

  if (!isInApp) return null;

  return (
    <div className="w-full max-w-[400px] mb-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 space-y-3">
      <div className="text-center">
        <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
          인앱 브라우저에서는 구글 로그인이 안 돼요
        </p>
      </div>
      <InAppBrowserGuideContent />
    </div>
  );
}

/**
 * 다이얼로그형 로그인 버튼 - 인앱 브라우저 감지 시 Clerk 모달 대신 안내 다이얼로그 표시
 * 일반 브라우저에서는 children(SignInButton)을 그대로 렌더링
 *
 * fallback: 인앱 브라우저일 때 SignInButton 대신 렌더링할 버튼 UI
 */
export function InAppSignInButton({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback: ReactNode;
}) {
  const [mounted, setMounted] = useState(false);
  const [isInApp, setIsInApp] = useState(false);
  const [open, setOpen] = useState(false);

  // navigator/mount 외부 시스템 동기화
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
    setIsInApp(isInAppBrowser());
  }, []);

  if (!mounted) return <>{fallback}</>;
  if (!isInApp) return <>{children}</>;

  return (
    <>
      <div onClick={() => setOpen(true)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen(true); }}>
        {fallback}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>인앱 브라우저에서는 로그인이 안 돼요</DialogTitle>
            <DialogDescription>
              외부 브라우저에서 열어주세요
            </DialogDescription>
          </DialogHeader>
          <InAppBrowserGuideContent />
        </DialogContent>
      </Dialog>
    </>
  );
}
